import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyColumnOrder,
  columnKeyOf,
  loadColumnOrder,
  moveColumnInOrder,
  normalizeColumnOrder,
  saveColumnOrder,
} from '../utils/columnLayout';

function keysFrom(columnsOrKeys, keyFn) {
  if (!columnsOrKeys?.length) return [];
  if (typeof columnsOrKeys[0] === 'string') return columnsOrKeys;
  return columnsOrKeys.map((c) => (keyFn ? keyFn(c) : columnKeyOf(c)));
}

/**
 * Personalized column order, persisted in localStorage.
 *
 * @param {string} storageKey
 * @param {Array<string|Object>} columnsOrKeys - column defs or key strings
 * @param {Function} [keyFn]
 */
const useColumnOrder = (storageKey, columnsOrKeys, keyFn) => {
  const keysSig = keysFrom(columnsOrKeys, keyFn).join('\0');
  const allKeys = useMemo(() => (keysSig ? keysSig.split('\0') : []), [keysSig]);

  const [columnOrder, setColumnOrderState] = useState(() =>
    normalizeColumnOrder(loadColumnOrder(storageKey), keysFrom(columnsOrKeys, keyFn))
  );

  useEffect(() => {
    setColumnOrderState((prev) => {
      const next = normalizeColumnOrder(prev.length ? prev : loadColumnOrder(storageKey), allKeys);
      if (prev.length === next.length && prev.every((k, i) => k === next[i])) return prev;
      if (storageKey) saveColumnOrder(storageKey, next);
      return next;
    });
  }, [allKeys, storageKey]);

  const persist = useCallback((next) => {
    const normalized = normalizeColumnOrder(next, allKeys);
    setColumnOrderState(normalized);
    saveColumnOrder(storageKey, normalized);
    return normalized;
  }, [allKeys, storageKey]);

  const moveColumn = useCallback((fromIndex, toIndex) => {
    setColumnOrderState((prev) => {
      const next = moveColumnInOrder(prev, fromIndex, toIndex);
      saveColumnOrder(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const resetOrder = useCallback(() => {
    persist(allKeys);
  }, [allKeys, persist]);

  const orderedColumns = useMemo(() => {
    if (!columnsOrKeys?.length || typeof columnsOrKeys[0] === 'string') return columnOrder;
    return applyColumnOrder(columnsOrKeys, columnOrder, keyFn);
  }, [columnsOrKeys, columnOrder, keyFn]);

  return {
    columnOrder,
    orderedColumns,
    moveColumn,
    resetOrder,
    setColumnOrder: persist,
  };
};

export default useColumnOrder;
